import { c as _c } from "react/compiler-runtime";
import { Typography, type SxProps } from '@mui/material';
import { Provider, type ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import { hasValidConditionValues } from '@/types/advanceFilter';
import type { BackupPolicyEditState } from './BackupPolicyEditState';
import { isPitrPolicyType } from './backupPoliciesTypes';
import { useBackupPolicySupportedProperties } from './backupPolicyProperties';
import { BackupPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { useBackupPolicyAvailableTypes } from './useBackupPolicyAvailableTypes';
const useSourceProviders = (policyType: BackupPolicyType): Provider[] | undefined => {
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD) {
    return [Provider.Aws];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    return [Provider.Azure];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY) {
    return [Provider.Aws, Provider.Gcp, Provider.MongoAtlas];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR) {
    return [Provider.Azure];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR) {
    return [Provider.Aws];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_BIGQUERY_NATIVE_PITR) {
    return [Provider.Gcp];
  }
  if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR) {
    return [Provider.Azure];
  }
  return undefined;
};
export const BackupPolicyConditionEditor = props => {
  const $ = _c(54);
  const nextButtonDisabledTooltip = props.wizardState.resourcesScopeType === "useConditions" && !hasValidConditionValues(props.wizardState.condition) ? "Please create a condition with all required fields" : "Please select at least one resource type";
  const getAvailableResourceTypes = useBackupPolicyAvailableTypes();
  let t0;
  if ($[0] !== getAvailableResourceTypes || $[1] !== props.wizardState.policyType || $[2] !== props.wizardState.vaultStorageTier) {
    t0 = getAvailableResourceTypes(props.wizardState.policyType, props.wizardState.vaultStorageTier);
    $[0] = getAvailableResourceTypes;
    $[1] = props.wizardState.policyType;
    $[2] = props.wizardState.vaultStorageTier;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const supportedResourceTypes = t0;
  const sourceProviders = useSourceProviders(props.wizardState.policyType);
  const selectedResourceTypes = props.wizardState.resourceTypes?.length ? props.wizardState.resourceTypes : supportedResourceTypes;
  const supportedProperties = useBackupPolicySupportedProperties(selectedResourceTypes, sourceProviders, props.wizardState.policyType);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      padding: 0
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props.wizardState.condition || $[6] !== props.wizardState.resourcesScopeType) {
    t2 = () => props.wizardState.resourcesScopeType !== "useConditions" || hasValidConditionValues(props.wizardState.condition);
    $[5] = props.wizardState.condition;
    $[6] = props.wizardState.resourcesScopeType;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props) {
    t3 = async () => {
      props.setNextStep(props.currentStep.next?.[0]);
    };
    $[8] = props;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== props) {
    t4 = name => props.setWizardState(state => ({
      ...state,
      name
    }));
    $[10] = props;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== props.wizardState.focusOnName || $[13] !== props.wizardState.name || $[14] !== t4) {
    t5 = <RenameEntityName focusOnMount={props.wizardState.focusOnName} label="Backup policy name" name={props.wizardState.name} onSubmit={t4} />;
    $[12] = props.wizardState.focusOnName;
    $[13] = props.wizardState.name;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== props.wizardState.policyType || $[17] !== props.wizardState.vaultStorageTier) {
    t6 = <BackupPolicySupportedDisclaimer policyType={props.wizardState.policyType} vaultStorageTier={props.wizardState.vaultStorageTier} />;
    $[16] = props.wizardState.policyType;
    $[17] = props.wizardState.vaultStorageTier;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== props) {
    t7 = condition => props.setWizardState({
      ...props.wizardState,
      condition
    });
    $[19] = props;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  let t9;
  if ($[21] !== props) {
    t8 = includedResources => props.setWizardState({
      ...props.wizardState,
      include: includedResources
    });
    t9 = excludedResources => props.setWizardState({
      ...props.wizardState,
      exclude: excludedResources
    });
    $[21] = props;
    $[22] = t8;
    $[23] = t9;
  } else {
    t8 = $[22];
    t9 = $[23];
  }
  let t10;
  if ($[24] !== props) {
    t10 = resourcesScopeType => props.setWizardState({
      ...props.wizardState,
      resourcesScopeType
    });
    $[24] = props;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      mb: "24px"
    };
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== props.wizardState.policyType || $[28] !== props.wizardState.resourceTypes) {
    t12 = <BackupPolicyConditionTitle resourceTypes={props.wizardState.resourceTypes} policyType={props.wizardState.policyType} sx={t11} />;
    $[27] = props.wizardState.policyType;
    $[28] = props.wizardState.resourceTypes;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_PITR || props.wizardState.policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_SQL_DATABASE_NATIVE_PITR ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too.";
  let t14;
  if ($[30] !== props.wizardState.condition || $[31] !== props.wizardState.exclude || $[32] !== props.wizardState.include || $[33] !== props.wizardState.policyType || $[34] !== props.wizardState.resourceTypes || $[35] !== props.wizardState.resourcesScopeType || $[36] !== selectedResourceTypes || $[37] !== sourceProviders || $[38] !== supportedProperties || $[39] !== t10 || $[40] !== t12 || $[41] !== t13 || $[42] !== t7 || $[43] !== t8 || $[44] !== t9) {
    t14 = <ResourcesSelectionEditor policyType={props.wizardState.policyType} selectedResourceTypes={props.wizardState.resourceTypes} supportedProviders={sourceProviders} supportedResourceTypes={selectedResourceTypes} condition={props.wizardState.condition} setCondition={t7} includedResources={props.wizardState.include} excludedResources={props.wizardState.exclude} setIncludedResources={t8} setExcludedResources={t9} resourcesScopeType={props.wizardState.resourcesScopeType} setResourcesScopeType={t10} supportedProperties={supportedProperties} conditionTitle={t12} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={t13} manualOverridesDescription="Manually include or exclude resources from your backup policy, regardless of dynamic selection." />;
    $[30] = props.wizardState.condition;
    $[31] = props.wizardState.exclude;
    $[32] = props.wizardState.include;
    $[33] = props.wizardState.policyType;
    $[34] = props.wizardState.resourceTypes;
    $[35] = props.wizardState.resourcesScopeType;
    $[36] = selectedResourceTypes;
    $[37] = sourceProviders;
    $[38] = supportedProperties;
    $[39] = t10;
    $[40] = t12;
    $[41] = t13;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== nextButtonDisabledTooltip || $[47] !== props.stepperLabels || $[48] !== t14 || $[49] !== t2 || $[50] !== t3 || $[51] !== t5 || $[52] !== t6) {
    t15 = <><StepContainer sx={t1} stepperLabels={props.stepperLabels} stepperIndex={0} nextButtonDisabledTooltip={nextButtonDisabledTooltip} canGoNext={t2} onNextClick={t3} navigationComponent={t5}>{t6}{t14}</StepContainer></>;
    $[46] = nextButtonDisabledTooltip;
    $[47] = props.stepperLabels;
    $[48] = t14;
    $[49] = t2;
    $[50] = t3;
    $[51] = t5;
    $[52] = t6;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  return t15;
};
export const BackupPolicyConditionTitle = t0 => {
  const $ = _c(7);
  const {
    resourceTypes,
    policyType,
    sx
  } = t0;
  if (!isPitrPolicyType(policyType) && policyType !== BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES) {
    let t1;
    if ($[0] !== sx) {
      t1 = <Typography variant="subtitle1" sx={sx}>Include resources based on these conditions:</Typography>;
      $[0] = sx;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== resourceTypes) {
    t1 = resourceTypes && <span className="font-medium">{resourceTypes?.map(_temp).join(", ")}{" "}</span>;
    $[2] = resourceTypes;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== sx || $[5] !== t1) {
    t2 = <Typography variant="subtitle1" sx={sx}>Include{" "}{t1}resources based on these conditions:</Typography>;
    $[4] = sx;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(x) {
  return ResourceTypes[x].displayName;
}